import React from "react";
import { Outlet, Link } from "react-router-dom";
import { Zap, Ban, ShieldCheck, MousePointerClick } from "lucide-react";

import ThemeToggle from "@/components/ThemeToggle";
import { CliqoMark } from "@/components/marketing/Brand";

// ---------------------------------------------------------------------------
// Feature highlight items shown on the brand panel
// ---------------------------------------------------------------------------

interface FeatureItem {
  icon: React.ElementType;
  title: string;
  description: string;
}

const FEATURES: FeatureItem[] = [
  {
    icon: Zap,
    title: "Instant shortening",
    description:
      "Short links in seconds — with expiry dates, password protection, and a live click count.",
  },
  {
    icon: Ban,
    title: "No subscription, ever",
    description:
      "Buy link credits once and use them whenever — no monthly bill, and credits never expire.",
  },
  {
    icon: ShieldCheck,
    title: "Secure by default",
    description:
      "OAuth2 sign-in and scoped, per-key API keys keep your account locked down.",
  },
];

/** Monospace spec label used across the panel. */
const MonoLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "",
}) => (
  <span
    className={`font-mono text-[11px] font-medium uppercase tracking-[0.22em] ${className}`}
  >
    {children}
  </span>
);

// ---------------------------------------------------------------------------
// Brand panel (left side) — always dark, theme-independent
// ---------------------------------------------------------------------------

const BrandPanel: React.FC = () => (
  <div className="relative hidden shrink-0 overflow-hidden bg-[#0a0a0b] lg:flex lg:w-[480px] lg:flex-col xl:w-[560px]">
    {/* White dot-grid texture (panel is always dark) */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 opacity-[0.6]"
      style={{
        backgroundImage:
          "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    />
    {/* Flame glow */}
    <span
      aria-hidden="true"
      className="absolute -bottom-24 -left-16 h-80 w-80 rounded-full bg-flame/25 blur-[110px]"
    />
    <span
      aria-hidden="true"
      className="absolute -right-24 top-10 h-72 w-72 rounded-full bg-white/[0.04] blur-3xl"
    />

    {/* Content */}
    <div className="relative z-10 flex flex-1 flex-col px-12 py-12">
      {/* Logo */}
      <Link
        to="/"
        className="mb-auto flex items-center gap-3 no-underline"
        aria-label="cliqo home"
      >
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm">
          <CliqoMark className="h-6 w-6 text-white" />
        </span>
        <span className="font-brand text-2xl font-extrabold tracking-tight text-white">
          cliqo
        </span>
      </Link>

      {/* Hero copy */}
      <div className="py-14">
        <MonoLabel className="text-flame">[ short links, kept simple ]</MonoLabel>
        <h2 className="mt-5 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white xl:text-5xl">
          Long links in.
          <br />
          <span className="relative inline-block">
            <span className="relative z-10">Short links</span>
            <span
              aria-hidden="true"
              className="absolute inset-x-0 bottom-1 z-0 h-3 -rotate-1 bg-flame/40"
            />
          </span>{" "}
          out.
        </h2>
        <p className="mt-5 max-w-sm leading-relaxed text-white/55">
          A focused link shortener with no monthly subscription and no bloat —
          just the essentials, done well.
        </p>
      </div>

      {/* Feature spec-list */}
      <ul className="mb-auto divide-y divide-white/10 border-y border-white/10">
        {FEATURES.map(({ icon: Icon, title, description }) => (
          <li key={title} className="flex items-start gap-4 py-4">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5">
              <Icon className="h-4 w-4 text-flame" aria-hidden="true" />
            </span>
            <div>
              <p className="text-sm font-semibold leading-snug text-white">
                {title}
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-white/45">
                {description}
              </p>
            </div>
          </li>
        ))}
      </ul>

      {/* Footer stat row */}
      <div className="mt-12 flex items-center gap-2 text-white/40">
        <MousePointerClick className="h-3.5 w-3.5 text-flame" />
        <MonoLabel>no subscription · no ads · no bloat</MonoLabel>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Auth Layout
// ---------------------------------------------------------------------------

const AuthLayout: React.FC = () => (
  <div className="flex min-h-screen bg-background">
    {/* ── Left brand panel ─────────────────────────────────────────────────── */}
    <BrandPanel />

    {/* ── Right form panel ─────────────────────────────────────────────────── */}
    <main className="relative flex flex-1 flex-col">
      {/* Faded engineered-grid texture at the top of the form side */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-line-grid mask-fade-edges opacity-40"
      />

      {/* Theme toggle — desktop (the mobile header has its own) */}
      <ThemeToggle className="absolute right-6 top-6 z-10 hidden lg:inline-flex" />

      {/* Mobile-only header */}
      <div className="relative flex items-center justify-between border-b border-border bg-card px-6 py-5 lg:hidden">
        <Link
          to="/"
          className="flex items-center gap-2.5 text-foreground no-underline hover:text-foreground"
          aria-label="cliqo home"
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
            <CliqoMark className="h-5 w-5 text-primary-foreground" />
          </span>
          <span className="font-brand text-lg font-extrabold tracking-tight text-foreground">
            cliqo
          </span>
        </Link>
        <ThemeToggle />
      </div>

      {/* Form area — vertically centred */}
      <div className="relative flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-[420px]">
          {/* Spec breadcrumb */}
          <div className="mb-8 hidden items-center gap-2 lg:flex">
            <span className="h-1.5 w-1.5 rounded-full bg-flame" />
            <MonoLabel className="text-muted-foreground">
              cliqo / secure access
            </MonoLabel>
          </div>
          {/*
           * The child route page (LoginPage, RegisterPage, etc.) is rendered
           * here via <Outlet />. Each page owns its heading and form elements.
           */}
          <Outlet />
        </div>
      </div>

      {/* Footer */}
      <footer className="relative px-6 pb-6 text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          &copy; {new Date().getFullYear()} cliqo
          {" · "}
          <a
            href="/privacy"
            className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            privacy
          </a>
          {" · "}
          <a
            href="/terms"
            className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            terms
          </a>
        </p>
      </footer>
    </main>
  </div>
);

export default AuthLayout;